import { useEffect } from 'react';

/**
 * Per-route SEO injector for the /memorize landing view (no active
 * session). Renders only inside the "setup form" branch — once a user
 * starts an active session, this component unmounts and the canonical
 * resets to the homepage on cleanup.
 *
 * On mount:
 *   - <title>, <meta name="description">
 *   - <link rel="canonical"> → https://alhudah.com/memorize
 *   - Open Graph + Twitter card meta
 *   - JSON-LD: HowTo schema describing the 4-step workflow
 *     (Listen → Repeat → Hide → Recall). Rich-result eligible.
 *
 * No external deps — same manual DOM upsert pattern as SurahSeo /
 * SurahIndexSeo.
 */
export function MemorizeSeo() {
  useEffect(() => {
    const url = 'https://alhudah.com/memorize';
    const title =
      'Memorize Quran Online Free — Hifz Tool with Audio Recitation · Alhudah';
    const description =
      'Free online Hifz tool to memorize the Quran ayah by ayah. ' +
      'Listen to Mishary Alafasy recitation, repeat, hide the text, ' +
      'and recall from memory. No account required.';

    document.title = title;
    upsertMeta('description', description);
    upsertLink('canonical', url);

    upsertMeta('og:title', 'Memorize Quran Online — Free Hifz Tool with Audio', 'property');
    upsertMeta('og:description', description, 'property');
    upsertMeta('og:url', url, 'property');
    upsertMeta('og:type', 'website', 'property');

    upsertMeta('twitter:title', 'Memorize Quran Online — Free Hifz Tool');
    upsertMeta('twitter:description', description);

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'How to memorize the Quran online',
      description:
        'A four-step Hifz workflow: listen to the ayah, repeat it aloud, ' +
        'hide the text, then recall it from memory.',
      url,
      inLanguage: ['en', 'ar'],
      tool: [
        { '@type': 'HowToTool', name: 'Alhudah Quran reader' },
        { '@type': 'HowToTool', name: 'Audio recitation by Mishary Alafasy' },
      ],
      step: [
        {
          '@type': 'HowToStep',
          position: 1,
          name: 'Listen',
          text: 'Play the ayah and listen attentively to the recitation by Mishary Rashid Alafasy. Repeat the audio as many times as you need.',
        },
        {
          '@type': 'HowToStep',
          position: 2,
          name: 'Repeat',
          text: 'Recite the ayah aloud along with the audio, focusing on tajweed and pronunciation. Match the reciter rhythm and pauses.',
        },
        {
          '@type': 'HowToStep',
          position: 3,
          name: 'Hide',
          text: 'Switch to hide mode and remove the Arabic text from view. Try reciting from memory.',
        },
        {
          '@type': 'HowToStep',
          position: 4,
          name: 'Recall',
          text: 'Recall the ayah from memory without audio. Mark it memorized and move to the next ayah.',
        },
      ],
    };
    upsertJsonLd('memorize-jsonld', jsonLd);

    return () => {
      upsertLink('canonical', 'https://alhudah.com/');
    };
  }, []);

  return null;
}

// ----- DOM helpers (mirrors SurahSeo / SurahIndexSeo) ----------------

function upsertMeta(
  name: string,
  content: string,
  attr: 'name' | 'property' = 'name',
): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id: string, payload: unknown): void {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}
